import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Alert,
  Grid,
  LinearProgress,
  Chip,
  Tooltip,
  Paper,
} from '@mui/material'
import {
  Edit as EditIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Warning as WarningIcon,
  TrendingDown as TrendingDownIcon,
  Inventory as InventoryIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon,
  AutorenewOutlined as AutoRefreshIcon,
  Close as CloseIcon,
  ShoppingCart,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { Line, Doughnut } from 'react-chartjs-2'
import { formatCompactCurrency } from '../utils/currency'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import { DataTable, StatusChip, TableColumn, TableAction } from '../components/ui'
import {
  useGetStockInfoQuery,
  useGetInventorySummaryQuery,
  useGetInventoryDashboardQuery,
  useGetLowStockVariantsQuery,
  useExportStockMutation,
  StockInfo
} from '../store/services/inventoryApi'
import StockAdjustmentDialog from '../components/inventory/StockAdjustmentDialog'
import BulkStockImportDialog from '../components/inventory/BulkStockImportDialog'
import LowStockAlertsDialog from '../components/inventory/LowStockAlertsDialog'
import StockMovementDialog from '../components/inventory/StockMovementDialog'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
)

const InventoryMUI: React.FC = () => {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [lowStockOnly, setLowStockOnly] = useState(false)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true)

  // RTK Query hooks
  const { data: stockData, isLoading, error: stockError, refetch: refetchStock } = useGetStockInfoQuery({
    page: page + 1,
    per_page: pageSize,
    search: searchTerm || undefined,
    category_id: categoryFilter || undefined,
    low_stock_only: lowStockOnly,
  })

  const { data: summary, refetch: refetchSummary } = useGetInventorySummaryQuery({
    category_id: categoryFilter || undefined,
  })

  const { data: dashboardData, isLoading: isDashboardLoading, refetch: refetchDashboard } = useGetInventoryDashboardQuery()

  const { data: lowStockVariants = [], refetch: refetchLowStock } = useGetLowStockVariantsQuery({
    threshold_multiplier: 1.0,
    limit: 50,
  })

  const [exportStock] = useExportStockMutation()
  
  // Dialog states
  const [selectedStock, setSelectedStock] = useState<StockInfo | null>(null)
  const [isAdjustmentOpen, setIsAdjustmentOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [isLowStockOpen, setIsLowStockOpen] = useState(false)
  const [isMovementOpen, setIsMovementOpen] = useState(false)
  
  const { enqueueSnackbar } = useSnackbar()

  // Auto-refresh every 15 minutes
  useEffect(() => {
    if (!autoRefreshEnabled) return

    const interval = setInterval(() => {
      refetchDashboard()
      refetchStock()
      refetchSummary()
      refetchLowStock()
      setLastRefresh(new Date())
    }, 15 * 60 * 1000) // 15 minutes

    return () => clearInterval(interval)
  }, [autoRefreshEnabled, refetchDashboard, refetchStock, refetchSummary, refetchLowStock])

  // Show error notifications
  useEffect(() => {
    if (stockError) {
      console.error('Failed to load inventory data:', stockError)
      enqueueSnackbar('Failed to load inventory data', { variant: 'error' })
    }
  }, [stockError, enqueueSnackbar])

  const handleManualRefresh = useCallback(() => {
    refetchDashboard()
    refetchStock()
    refetchSummary()
    refetchLowStock()
    setLastRefresh(new Date())
  }, [refetchDashboard, refetchStock, refetchSummary, refetchLowStock])

  const handleStockAdjustment = (stock: StockInfo) => {
    setSelectedStock(stock)
    setIsAdjustmentOpen(true)
  }

  const handleViewMovements = (stock: StockInfo) => {
    setSelectedStock(stock)
    setIsMovementOpen(true)
  }

  const handleExport = async () => {
    try {
      const blob = await exportStock({
        category_id: categoryFilter || undefined,
      }).unwrap()
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `inventory_export_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      enqueueSnackbar('Inventory data exported successfully', { variant: 'success' })
    } catch (error) {
      console.error('Export error:', error)
      enqueueSnackbar('Failed to export inventory data', { variant: 'error' })
    }
  }

  const handleAdjustmentSuccess = () => {
    refetchStock()
    refetchSummary()
    refetchLowStock()
    setIsAdjustmentOpen(false)
    setSelectedStock(null)
  }

  const handleImportSuccess = () => {
    refetchStock()
    refetchSummary()
    refetchLowStock()
    setIsImportOpen(false)
  }

  const columns: TableColumn[] = [
    {
      id: 'product',
      label: 'Product',
      width: 200,
      render: (_, row: StockInfo) => (
        <Box sx={{ maxWidth: 300 }}>
          {row.is_set_selling && (
            <Chip 
              label={`SET: ${row.set_size} pcs`} 
              size="small" 
              color="success" 
              variant="outlined"
              sx={{ mb: 1, height: 20, fontSize: '0.65rem', fontWeight: 700 }} 
            />
          )}
          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
            {row.product_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word', lineHeight: 1.2, display: 'block', mt: 0.5 }}>
            {row.variant_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
            SKU: {row.variant_sku}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'current_stock',
      label: 'Current',
      width: 100,
      align: 'right',
      render: (_, row: StockInfo) => (
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color={row.is_low_stock ? 'error.main' : 'text.primary'}
          >
            {row.current_stock.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(row as any).is_set_selling ? 'Sets' : 'Units'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'reserved_stock',
      label: 'Reserved',
      width: 100,
      align: 'right',
      render: (_, row: StockInfo) => (
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="warning.main">
            {row.reserved_stock.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(row as any).is_set_selling ? 'Sets' : 'Units'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'available_stock',
      label: 'Available',
      width: 100,
      align: 'right',
      render: (_, row: StockInfo) => (
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            variant="body2"
            fontWeight={500}
            color={row.available_stock <= 0 ? 'error.main' : 'success.main'}
          >
            {row.available_stock.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(row as any).is_set_selling ? 'Sets' : 'Units'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'minimum_stock',
      label: 'Min',
      width: 80,
      align: 'right',
      render: (_, row: StockInfo) => (
        <Typography variant="body2" color="text.secondary">
          {row.minimum_stock.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      width: 120,
      render: (_, row: StockInfo) => {
        if (row.available_stock <= 0) {
          return <StatusChip status="error" label="Out of Stock" />
        } else if (row.is_low_stock) {
          return <StatusChip status="warning" label="Low Stock" />
        } else {
          return <StatusChip status="success" label="In Stock" />
        }
      },
    },
    {
      id: 'last_updated',
      label: 'Updated',
      width: 100,
      render: (_, row: StockInfo) => (
        <Typography variant="caption" color="text.secondary">
          {new Date(row.last_updated).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
        </Typography>
      ),
    },
  ]

  const actions: TableAction[] = [
    {
      icon: <EditIcon />,
      label: 'Adjust Stock',
      onClick: handleStockAdjustment,
      color: 'primary',
    },
    {
      icon: <AssessmentIcon />,
      label: 'View Movements',
      onClick: handleViewMovements,
      color: 'secondary',
    },
    {
      icon: <ShoppingCart />,
      label: 'View on Store',
      onClick: (row: StockInfo) => {
        const baseUrl = (import.meta as any).env.VITE_CUSTOMER_PORTAL_URL
        if (baseUrl) {
          window.open(`${baseUrl}/products/${row.product_id}`, '_blank')
        } else {
          enqueueSnackbar('Front store URL not configured', { variant: 'error' })
        }
      },
      color: 'success',
    },
  ]

  // Chart configurations
  const inventoryTrendChartData = {
    labels: dashboardData?.inventory_value_trend.map(item => item.month) || [],
    datasets: [
      {
        label: 'Inventory Value',
        data: dashboardData?.inventory_value_trend.map(item => item.value) || [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
      },
    ],
  }

  const inventoryTrendChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: '12-Month Inventory Value Trend',
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: {
          callback: function(value: any) {
            return formatCompactCurrency(value)
          }
        }
      }
    }
  }

  const velocityChartData = {
    labels: ['Fast Moving', 'Medium Moving', 'Slow Moving'],
    datasets: [
      {
        data: [
          dashboardData?.movement_velocity.fast_moving.count || 0,
          dashboardData?.movement_velocity.medium_moving.count || 0,
          dashboardData?.movement_velocity.slow_moving.count || 0,
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(255, 99, 132, 0.8)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      },
    ],
  }

  const velocityChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: 'Movement Velocity Breakdown',
      },
    },
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section with Dhanshika Exports Branding */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
          color: 'white',
          borderRadius: 3,
          boxShadow: '0 4px 16px rgba(33, 150, 243, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
              Inventory Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Advanced inventory analytics and stock management for Dhanshika Exports
            </Typography>
          </Box>
          
          {/* Action Buttons */}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Tooltip title={`Auto-refresh is ${autoRefreshEnabled ? 'enabled' : 'disabled'}`}>
              <Button
                onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                variant={autoRefreshEnabled ? 'contained' : 'outlined'}
                color={autoRefreshEnabled ? 'success' : 'inherit'}
                size="small"
                startIcon={<AutoRefreshIcon />}
                sx={{ 
                  color: autoRefreshEnabled ? 'white' : 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: autoRefreshEnabled ? 'success.dark' : 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Auto-refresh
              </Button>
            </Tooltip>
            
            <Button
              onClick={handleManualRefresh}
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Refresh
            </Button>
            
            <Button
              onClick={() => setIsLowStockOpen(true)}
              variant="outlined"
              size="small"
              startIcon={<WarningIcon />}
              sx={{ 
                color: lowStockVariants.length > 0 ? '#ff9800' : 'white',
                borderColor: lowStockVariants.length > 0 ? '#ff9800' : 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: lowStockVariants.length > 0 ? '#ff9800' : 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Alerts {lowStockVariants.length > 0 && `(${lowStockVariants.length})`}
            </Button>
            
            <Button
              onClick={() => setIsImportOpen(true)}
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Import
            </Button>
            
            <Button
              onClick={handleExport}
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Export
            </Button>
          </Stack>
        </Box>

        {/* Status Indicators */}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Typography>
          {autoRefreshEnabled && (
            <Chip 
              label="Auto-refresh: 15 min" 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontSize: '0.75rem',
              }} 
            />
          )}
          {isLoading && (
            <Chip 
              label="Loading..." 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontSize: '0.75rem',
              }} 
            />
          )}
        </Stack>
      </Paper>
      {/* Loading indicator */}
      {isDashboardLoading && (
        <LinearProgress 
          sx={{ 
            mb: 2, 
            borderRadius: 1,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
            },
          }} 
        />
      )}

      {/* Enhanced Summary Cards with Dhanshika Exports Styling */}
      {dashboardData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(33, 150, 243, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(33, 150, 243, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'primary.main',
                    color: 'white',
                  }}
                >
                  <InventoryIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {dashboardData.summary.total_variants.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Total Variants
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(76, 175, 80, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(76, 175, 80, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'success.main',
                    color: 'white',
                  }}
                >
                  <AssessmentIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {dashboardData.summary.total_stock_quantity.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Stock Quantity
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(76, 175, 80, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(76, 175, 80, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'success.main',
                    color: 'white',
                  }}
                >
                  <TrendingUpIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {formatCompactCurrency(dashboardData.summary.total_stock_value)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Stock Value
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(244, 67, 54, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(244, 67, 54, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'secondary.main',
                    color: 'white',
                  }}
                >
                  <SpeedIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="secondary.main">
                    {dashboardData.summary.inventory_turnover_ratio.toFixed(1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Turnover Ratio
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255, 152, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(255, 152, 0, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'warning.main',
                    color: 'white',
                  }}
                >
                  <WarningIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {dashboardData.summary.low_stock_variants.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Low Stock Items
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(244, 67, 54, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(244, 67, 54, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'error.main',
                    color: 'white',
                  }}
                >
                  <TrendingDownIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {dashboardData.summary.out_of_stock_variants.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Out of Stock
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255, 152, 0, 0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(255, 152, 0, 0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'warning.main',
                    color: 'white',
                  }}
                >
                  <InventoryIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {dashboardData.summary.overstocked_variants.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Overstocked
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Enhanced Charts and Analytics */}
      {dashboardData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Inventory Value Trend Chart */}
          <Grid item xs={12} lg={8}>
            <Card 
              sx={{ 
                p: 3, 
                height: 400, 
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                },
                transition: 'box-shadow 0.2s ease-in-out',
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                12-Month Inventory Value Trend
              </Typography>
              <Line data={inventoryTrendChartData} options={inventoryTrendChartOptions} />
            </Card>
          </Grid>

          {/* Movement Velocity Breakdown */}
          <Grid item xs={12} lg={4}>
            <Card 
              sx={{ 
                p: 3, 
                height: 400, 
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                },
                transition: 'box-shadow 0.2s ease-in-out',
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                Movement Velocity Breakdown
              </Typography>
              <Box sx={{ height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Doughnut data={velocityChartData} options={velocityChartOptions} />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(75, 192, 192, 0.8)' }} />
                      <Typography variant="body2" fontWeight={500}>Fast Moving</Typography>
                    </Stack>
                    <Chip 
                      label={`${dashboardData.movement_velocity.fast_moving.percentage}%`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(255, 206, 86, 0.8)' }} />
                      <Typography variant="body2" fontWeight={500}>Medium Moving</Typography>
                    </Stack>
                    <Chip 
                      label={`${dashboardData.movement_velocity.medium_moving.percentage}%`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'rgba(255, 99, 132, 0.8)' }} />
                      <Typography variant="body2" fontWeight={500}>Slow Moving</Typography>
                    </Stack>
                    <Chip 
                      label={`${dashboardData.movement_velocity.slow_moving.percentage}%`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </Box>
                </Stack>
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

        {/* Top Categories and Brands */}
        {dashboardData && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Top 5 Categories by Value */}
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <CategoryIcon sx={{ mr: 1 }} />
                  Top 5 Categories by Value
                </Typography>
                <Stack spacing={2}>
                  {dashboardData.top_categories.map((category, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {category.category_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCompactCurrency(category.stock_value)} ({category.percentage}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={category.percentage}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>

            {/* Top 5 Brands by Value */}
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon sx={{ mr: 1 }} />
                  Top 5 Brands by Value
                </Typography>
                <Stack spacing={2}>
                  {dashboardData.top_brands.map((brand, index) => (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {brand.brand_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCompactCurrency(brand.stock_value)} ({brand.percentage}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={brand.percentage}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        )}

      {/* Enhanced Low Stock Alert */}
      {lowStockVariants.length > 0 && (
        <Alert
          severity="warning"
          sx={{ 
            mb: 3,
            borderRadius: 2,
            border: '1px solid rgba(255, 152, 0, 0.2)',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem',
            },
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setIsLowStockOpen(true)}
              sx={{ 
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                },
              }}
            >
              View Details
            </Button>
          }
        >
          <Typography variant="body1" fontWeight={600}>
            Stock Alert: {lowStockVariants.length} variant{lowStockVariants.length !== 1 ? 's' : ''} running low on stock
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
            Immediate attention required to prevent stockouts
          </Typography>
        </Alert>
      )}

      {/* Enhanced Filters Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
          Search & Filters
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            placeholder="Search by product name, variant, or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />,
            }}
            sx={{ 
              minWidth: 320, 
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          <TextField
            select
            label="Category Filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            size="small"
            sx={{ 
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          >
            <MenuItem value="">All Categories</MenuItem>
            {summary?.categories_summary.map((category) => (
              <MenuItem key={category.category_id} value={category.category_id}>
                {category.category_name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant={lowStockOnly ? 'contained' : 'outlined'}
            onClick={() => setLowStockOnly(!lowStockOnly)}
            startIcon={<WarningIcon />}
            color={lowStockOnly ? 'warning' : 'primary'}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              minWidth: 140,
            }}
          >
            Low Stock Only
          </Button>
          <Tooltip title="More filtering options coming soon">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 2,
                color: 'text.secondary',
                borderColor: 'rgba(0,0,0,0.2)',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                },
              }}
            >
              More Filters
            </Button>
          </Tooltip>
        </Stack>
        
        {/* Active Filters Display */}
        {(searchTerm || categoryFilter || lowStockOnly) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Active Filters:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {searchTerm && (
                <Chip
                  label={`Search: "${searchTerm}"`}
                  size="small"
                  onDelete={() => setSearchTerm('')}
                  deleteIcon={<CloseIcon />}
                  color="primary"
                  variant="outlined"
                />
              )}
              {categoryFilter && (
                <Chip
                  label={`Category: ${summary?.categories_summary.find(c => c.category_id === categoryFilter)?.category_name || categoryFilter}`}
                  size="small"
                  onDelete={() => setCategoryFilter('')}
                  deleteIcon={<CloseIcon />}
                  color="primary"
                  variant="outlined"
                />
              )}
              {lowStockOnly && (
                <Chip
                  label="Low Stock Only"
                  size="small"
                  onDelete={() => setLowStockOnly(false)}
                  deleteIcon={<CloseIcon />}
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>
        )}
      </Card>

      {/* Inventory Levels Table */}
      <Card 
        sx={{ 
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" fontWeight={600} color="primary.main">
            Inventory Levels
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {stockData?.total || 0} total variants • Showing {stockData?.items?.length || 0} items
          </Typography>
        </Box>
        <Box sx={{ overflow: 'auto' }}>
          <DataTable
            columns={columns}
            rows={stockData?.items || []}
            actions={actions}
            isLoading={isLoading}
            emptyMessage="No inventory data found. Import stock data to get started."
            page={page}
            rowsPerPage={pageSize}
            totalRows={stockData?.total || 0}
            onPageChange={setPage}
            onRowsPerPageChange={setPageSize}
            rowsPerPageOptions={[10, 20, 50, 100]}
            getRowId={(row: StockInfo) => row.variant_id}
          />
        </Box>
      </Card>

      {/* Dialogs */}
      <StockAdjustmentDialog
        open={isAdjustmentOpen}
        stock={selectedStock}
        onClose={() => {
          setIsAdjustmentOpen(false)
          setSelectedStock(null)
        }}
        onSuccess={handleAdjustmentSuccess}
      />

      <BulkStockImportDialog
        open={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onSuccess={handleImportSuccess}
      />

      <LowStockAlertsDialog
        open={isLowStockOpen}
        lowStockVariants={lowStockVariants}
        onClose={() => setIsLowStockOpen(false)}
        onRefresh={refetchLowStock}
      />

      <StockMovementDialog
        open={isMovementOpen}
        stock={selectedStock}
        onClose={() => {
          setIsMovementOpen(false)
          setSelectedStock(null)
        }}
      />
    </Box>
  )
}

export default InventoryMUI